// packages/core/lib/features/programs/utils/status-modal.ts
import {
  ClientProgramStatus,
  ClientProgramStatusRank,
  Program,
  ProgramClientRefusalReason,
  ProgramNeededProperties,
} from '@hellodarwin/core/lib/features/entities/programs-entities';
import dayjs from 'dayjs';

/**
 * Given an initial and a next ClientProgramStatus, determine which
 * ProgramNeededProperties are required and prefill sensible defaults
 * based on the provided Program.
 *
 * If either status is undefined or the transition doesn't cross any
 * thresholds, returns shouldOpen = false.
 */
export function getModalRequirementsForStatusChange(
  initialStatus: ClientProgramStatus | undefined,
  nextStatus: ClientProgramStatus | undefined,
  program: Pick<
    Program,
    | 'program_targeted_amount'
    | 'program_applied_at'
    | 'program_application_amount'
    | 'program_result_at'
    | 'program_accepted_amount'
    | 'program_refusal_reason'
  >,
): { shouldOpen: boolean; neededProperties: ProgramNeededProperties } {
  const neededProperties: ProgramNeededProperties = {
    draft_first_version: {
      amount: null,
    },
    submit_application: {
      time: null,
      amount: null,
    },
    decisions_made: {
      time: null,
      amount: null,
      refusal_reason: null,
    },
  };

  if (initialStatus == null || nextStatus == null) {
    return { shouldOpen: false, neededProperties };
  }

  const initialVal = ClientProgramStatusRank[initialStatus];
  const nextVal = ClientProgramStatusRank[nextStatus];

  if (initialVal === undefined || nextVal === undefined) {
    return { shouldOpen: false, neededProperties };
  }

  if (
    initialVal <
      ClientProgramStatusRank[ClientProgramStatus.DraftFirstVersion] &&
    nextVal >= ClientProgramStatusRank[ClientProgramStatus.DraftFirstVersion]
  ) {
    neededProperties.draft_first_version.amount = undefined;
  }

  if (
    initialVal <
      ClientProgramStatusRank[ClientProgramStatus.SubmitApplication] &&
    nextVal >= ClientProgramStatusRank[ClientProgramStatus.SubmitApplication]
  ) {
    neededProperties.submit_application.time = undefined;
    neededProperties.submit_application.amount = undefined;
  }

  if (
    initialVal < ClientProgramStatusRank[ClientProgramStatus.DecisionsMade] &&
    nextVal >= ClientProgramStatusRank[ClientProgramStatus.DecisionsMade]
  ) {
    neededProperties.decisions_made.time = undefined;
    neededProperties.decisions_made.amount = undefined;
    neededProperties.decisions_made.refusal_reason = undefined;
  }

  // Prefill defaults from the program
  if (
    neededProperties.draft_first_version.amount === undefined &&
    program.program_targeted_amount
  ) {
    neededProperties.draft_first_version.amount =
      program.program_targeted_amount;
  }
  if (
    neededProperties.submit_application.time === undefined &&
    program.program_applied_at
  ) {
    neededProperties.submit_application.time = program.program_applied_at
      ? dayjs(program.program_applied_at)
      : undefined;
  }
  if (
    neededProperties.submit_application.amount === undefined &&
    program.program_application_amount
  ) {
    neededProperties.submit_application.amount =
      program.program_application_amount;
  }
  if (
    neededProperties.decisions_made.time === undefined &&
    program.program_result_at
  ) {
    neededProperties.decisions_made.time = program.program_result_at
      ? dayjs(program.program_result_at)
      : undefined;
  }
  if (
    neededProperties.decisions_made.amount === undefined &&
    program.program_accepted_amount
  ) {
    neededProperties.decisions_made.amount = program.program_accepted_amount;
  }
  if (
    neededProperties.decisions_made.refusal_reason === undefined &&
    program.program_refusal_reason
  ) {
    neededProperties.decisions_made.refusal_reason =
      program.program_refusal_reason as ProgramClientRefusalReason;
  }

  const shouldOpen =
    neededProperties.draft_first_version.amount === undefined ||
    neededProperties.submit_application.time === undefined ||
    neededProperties.submit_application.amount === undefined ||
    neededProperties.decisions_made.time === undefined ||
    neededProperties.decisions_made.amount === undefined ||
    neededProperties.decisions_made.refusal_reason === undefined;

  return { shouldOpen, neededProperties };
}
